/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useMsal } from "@azure/msal-react";
import { Capacitor } from "@capacitor/core";
import type {
  AccountManagementContextValue,
  AccountManagementDownloadStatementParams,
} from "@farmlands/account-management";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { saveBlobToDevice, viewBlobInBrowser } from "../../../services/file-manager";
import { selectRemoteConfig } from "../../../services/firebase/remote-config/remoteConfig.slice";
import { myFarmlandsApi } from "../../../services/myfarmlands-api";
import { error as logError } from "../../../util/log";
import useToast from "../../../util/useToast";
import { passwordResetConfig } from "../../azure-b2c/config";
import { selectIdToken } from "../../azure-b2c/storage";
import { PAYMENTS_ROUTES } from "../payments/constants";

import { useActiveAccountQuery } from "./useActiveAccountQuery";
import { useSelectedAccountQuery } from "./useSelectedAccountQuery";

export type UseAccountManagementContextValueOptions = {
  /**
   * Resolve the account from {@link availableAccount} in `/accounts`, independent of the
   * selection — for user-scoped views that must work for every user regardless of the selected
   * account type. Defaults to `false` (uses the matched selection).
   */
  userScoped?: boolean;
};

export type UseAccountManagementContextValueResult = {
  /** Fully-built context value for `<AccountManagementProvider value={...}>`. */
  contextValue: AccountManagementContextValue;
  /** True while the underlying `/accounts` request is in flight. */
  isLoading: boolean;
  /** Error from the underlying `/accounts` request, if any. Wire to an `<AppError>` fallback. */
  error: unknown;
  /** Re-fires the `/accounts` request. Pass to `<AppError onTryAgainClick={...}>`. */
  refetch: () => void;
};

export const useAccountManagementContextValue = ({
  userScoped = false,
}: UseAccountManagementContextValueOptions = {}): UseAccountManagementContextValueResult => {
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const { selectedAccount, availableAccount, accountId, isLoading, error, refetch } =
    useSelectedAccountQuery();
  const {
    emailPreferencesCentreUrl,
    contactUrl,
    directDebitRegisterUrl,
    accountAndCardFeesTermsUrl,
    farmlandsStoreLocatorUrl,
    statementsAndPaymentsUrl,
    permissionsChecksEnabled,
  } = useSelector(selectRemoteConfig);
  const idToken = useSelector(selectIdToken);

  const account = userScoped ? availableAccount : selectedAccount;

  const username =
    (activeAccount?.idTokenClaims?.emails as unknown as string) ?? activeAccount?.username ?? "";

  const { activeAccountDetails, isReady } = useActiveAccountQuery(account?.accountNumber ?? null);

  let baseUrl = process.env.REACT_APP_AZURE_B2C_LOGIN_REDIRECT_URI;
  if (baseUrl?.endsWith("/")) {
    baseUrl = baseUrl.slice(0, -1);
  }
  const paymentReturnUrl = `${baseUrl}${PAYMENTS_ROUTES.BASE}`;
  const { notify } = useToast();

  const contextValue = useMemo<AccountManagementContextValue>(
    () => ({
      apiClient: myFarmlandsApi,
      identity: {
        accountId,
        username,
        idToken: idToken ?? null,
      },
      account: {
        selectedAccount: account,
        activeAccountDetails,
        isReady,
      },
      config: {
        contactUrl,
        emailPreferencesCentreUrl,
        directDebitRegisterUrl,
        farmlandsStoreLocatorUrl,
        accountAndCardFeesTermsUrl,
        paymentReturnUrl,
        statementsAndPaymentsUrl,
        permissionsApiBasePath: process.env.REACT_APP_PERMISSIONS_API_BASE_PATH ?? "",
        permissionsChecksEnabled: permissionsChecksEnabled,
      },
      onChangePassword: () => instance.loginRedirect(passwordResetConfig),
      openStatementPdfViewWindow: () =>
        Capacitor.isNativePlatform() ? null : window.open("about:blank", "_blank"),
      onDownloadStatement: async ({
        blob,
        filename,
        contentType,
        format,
        viewWindow,
      }: AccountManagementDownloadStatementParams) => {
        try {
          if (format === "pdf") {
            await viewBlobInBrowser({ blob, filename, contentType, viewWindow });
            return;
          }

          await saveBlobToDevice({ blob, filename, contentType });

          if (Capacitor.isNativePlatform()) {
            notify("Statement saved successfully.", { type: "success" });
          }
        } catch (err) {
          logError(err instanceof Error ? err.message : "Statement delivery failed", {
            format,
            filename,
          });
          throw err;
        }
      },
      onError: (err: Error, errContext?: Record<string, unknown>) => {
        logError(err.message, errContext);
      },
    }),
    [
      accountId,
      username,
      idToken,
      account,
      activeAccountDetails,
      isReady,
      contactUrl,
      emailPreferencesCentreUrl,
      directDebitRegisterUrl,
      farmlandsStoreLocatorUrl,
      accountAndCardFeesTermsUrl,
      paymentReturnUrl,
      permissionsChecksEnabled,
      instance,
      statementsAndPaymentsUrl,
      notify,
    ]
  );

  return { contextValue, isLoading, error, refetch };
};
